import { FaShieldAlt, FaCode, FaRocket, FaPlug, FaDatabase, FaChartLine } from 'react-icons/fa';

export const salesforceServiceData = {
  slug: 'salesforce',
  title: 'Salesforce Developer Phoenix AZ | Apex, LWC & Integration Expert',
  metaDescription: 'Senior Salesforce developer in Phoenix, Arizona. Custom Apex, Lightning Web Components, multi-system integrations, and data governance for businesses that need their org to scale. Book a free consultation.',
  keywords: [
    'Salesforce developer Phoenix',
    'Salesforce developer Arizona',
    'Apex developer Arizona',
    'Lightning Web Components developer',
    'Salesforce integration Phoenix',
    'Salesforce consultant Arizona',
    'Custom CRM solutions Arizona',
    'Salesforce automation Phoenix'
  ],

  hero: {
    kicker: 'Salesforce Development',
    title: 'Senior Salesforce development',
    accent: 'for Arizona businesses.',
    subtitle: 'Apex, Lightning Web Components, and multi-system integrations from an engineer who has architected and owned a production Salesforce org end to end.',
    lede: 'Most Salesforce orgs become a tangle of clicks-built automation no one fully understands. I architect the parts that matter — the Apex, the Lightning Web Components, the integrations — and own them end to end, the way I own a 50-user production org today. What follows is a short record of the work. The rest is a conversation.',
    meta: [
      { k: 'Location', v: 'Phoenix, AZ' },
      { k: 'Experience', v: '4+ years' },
      { k: 'Platform', v: 'Sole owner · 50-user org' },
      { k: 'Focus', v: 'Apex · LWC · Integrations' }
    ],
    stats: [
      { number: '4+ yrs', label: 'Salesforce Development' },
      { number: '50-user', label: 'Org Architected & Owned' },
      { number: '100k+', label: 'Records Cleansed & Governed' }
    ]
  },

  integrations: {
    title: 'Integrations',
    caption: 'Systems I have connected to Salesforce in production — via REST/SOAP callouts, middleware, and native packages.',
    items: [
      { name: 'Workday', method: 'REST / SOAP' },
      { name: 'Calendly', method: 'REST + webhooks' },
      { name: 'Slack', method: 'REST' },
      { name: 'DocuSign', method: 'API / package' },
      { name: 'Pardot / Account Engagement', method: 'Native' },
      { name: 'Zapier', method: 'Middleware' }
    ]
  },

  architecture: {
    title: 'Inside the forecasting engine',
    caption: 'The revenue & margin model leadership uses for financial planning — abstracted from production work.',
    flow: [
      {
        label: 'Inputs',
        items: [
          'Opportunities, weighted by win probability',
          'Placed resources & logged hours',
          'Net working days',
          'Committed vs. at-risk scenarios'
        ]
      },
      {
        label: 'Apex Engine',
        items: [
          '~6 custom objects, trigger + batch orchestrated',
          'Probability-weighted projection',
          'Self-corrects as hours post',
          'Reconciles forecast against actuals'
        ]
      },
      {
        label: 'Outputs',
        items: [
          'Revenue, margin & margin %',
          'Every grain: weekly → all-time',
          'At-risk vs. committed views',
          'Board-ready financial planning'
        ]
      }
    ]
  },

  whyChooseUs: {
    title: 'Why Work With Me',
    benefits: [
      {
        icon: <FaShieldAlt />,
        title: 'Sole Platform Ownership',
        description: 'I have architected and owned a 50-user Salesforce org end to end — finance, sales, staffing, marketing, and recruiting. You get one accountable engineer who designs, builds, deploys, and maintains, with no handoffs.'
      },
      {
        icon: <FaCode />,
        title: 'Production-Grade Apex',
        description: 'Triggers, Batch, Queueable, Future, and Schedulable Apex built to scale — with governor-limit optimization and real test coverage so your deployments hold up in production.'
      },
      {
        icon: <FaRocket />,
        title: 'Modern Lightning UX',
        description: 'Custom Lightning Web Components, Flow-embedded UI, and public Experience Cloud (LWR) sites that let staff and external users self-serve — without buying extra licenses.'
      },
      {
        icon: <FaPlug />,
        title: 'Multi-System Integrations',
        description: 'REST and SOAP integrations connecting Salesforce to the tools you already run — Workday, Calendly, Slack, DocuSign, Pardot, and middleware like Zapier.'
      },
      {
        icon: <FaDatabase />,
        title: 'Trustworthy Data',
        description: 'Data migrations, governance, and cleanup at scale — including a 100k+ record Pardot overhaul — plus backup and restore, so the numbers leadership reports on are accurate.'
      },
      {
        icon: <FaChartLine />,
        title: 'Financial Systems Expertise',
        description: 'I built the revenue and margin forecasting engine my organization\'s leadership uses for financial planning. If your business runs on numbers in Salesforce, I can make them accurate and automated.'
      }
    ]
  },

  process: {
    title: 'How I Work',
    description: 'A systematic approach that ensures your Salesforce solution delivers measurable value.',
    steps: [
      {
        title: 'Discovery & Analysis',
        description: 'We dig into your current workflows, uncover hidden inefficiencies, and define clear success metrics. This ensures every feature I build maps directly to your operations and bottom line.'
      },
      {
        title: 'Smart Design & Development',
        description: 'I lean on proven Salesforce features first, then write custom Apex and LWC only where they earn their keep. That keeps cost and complexity down while delivering exactly what your business needs.'
      },
      {
        title: 'Testing & Deployment',
        description: 'Everything is tested in a sandbox with real Apex test coverage before it goes live, then deployed with Salesforce CLI and Git. You get hands-on training and documentation so your team adopts it from day one.'
      },
      {
        title: 'Optimization & Support',
        description: 'Your org keeps gaining value through performance monitoring, proactive optimization, and responsive support as your business evolves and scales.'
      }
    ]
  },

  caseStudies: {
    title: 'Selected Work',
    description: 'Real production solutions architected, built, and deployed.',
    items: [
      {
        title: 'Revenue & Margin Forecasting Engine',
        context: '50-user professional services firm',
        problem: 'Leadership had no reliable way to forecast revenue and margin across committed and at-risk work.',
        solution: 'Architected an Apex-driven forecasting engine across 6+ custom objects, modeling at-risk and committed scenarios over net working days and weighting by opportunity probability. Built it to self-correct as resources log hours, reconciling forecast against actuals.',
        result: 'Became the focal point of company financial planning — surfacing revenue, margin, and margin % at every grain from weekly to all-time.'
      },
      {
        title: 'Full Org Split & Rebuild',
        context: 'Company restructure',
        problem: 'Half the company needed to move into a separate Salesforce org without disrupting users or breaking existing links.',
        solution: 'Served as primary architect for a complete org split — stood up a fresh org, migrated all data, and preserved the original domain and usernames so nothing broke.',
        result: 'Zero broken links and no user disruption through the cutover.'
      },
      {
        title: 'Release-Compliance Upgrade',
        context: 'Salesforce consulting client',
        problem: 'Deprecated API features and a backlog of failing Apex tests were blocking the client from deploying to production ahead of Salesforce enforcement deadlines.',
        solution: 'Performed a platform version upgrade, remediated retired Apex methods and deprecated features, and cleared the failing-test backlog.',
        result: 'Restored the client\'s ability to ship to production before the compliance deadline.'
      }
    ]
  },

  technologies: {
    title: 'Salesforce Technologies I Work In',
    items: [
      'Apex (Batch, Queueable, Schedulable)',
      'Lightning Web Components',
      'Aura & Visualforce',
      'Flows & Platform Events',
      'SOQL / SOSL',
      'REST & SOAP Integrations',
      'Experience Cloud (LWR)',
      'Pardot / Account Engagement',
      'Sales & Service Cloud',
      'Data Loader & Migrations',
      'Salesforce CLI & Git',
      'Governor-Limit Optimization'
    ]
  },

  results: {
    title: 'Proven on the Platform',
    items: [
      {
        metric: '6+',
        description: 'Apex-driven objects powering a live revenue & margin forecasting engine'
      },
      {
        metric: '100k+',
        description: 'Contacts and leads cleansed in a Pardot data governance overhaul'
      },
      {
        metric: '~70k',
        description: 'Unqualified records retired to restore marketing data integrity'
      },
      {
        metric: 'Zero',
        description: 'Broken links or user disruption during a full org split and migration'
      }
    ]
  },

  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'What kinds of Salesforce projects do you take on?',
        answer: 'Everything from custom Apex and Lightning Web Components to multi-system integrations (Workday, Calendly, Slack, DocuSign, Pardot), Experience Cloud sites, data migrations and cleanup, and complex automation with Flow. If you have an existing org that needs to scale — or a problem no one else has cracked — that is exactly the work I do.'
      },
      {
        question: 'How long does a typical Salesforce project take?',
        answer: 'It depends entirely on scope. A focused automation, integration fix, or report build can land in a day or two, while a comprehensive custom solution may take several weeks. Most clients see their first improvement within the first week.'
      },
      {
        question: 'Can you integrate Salesforce with our existing systems?',
        answer: 'Yes. I build REST and SOAP integrations connecting Salesforce to HR, scheduling, e-signature, and marketing platforms — including Workday, Calendly, Slack, DocuSign, and Pardot — as well as middleware like Zapier.'
      },
      {
        question: 'Do you provide training for our team?',
        answer: 'Always. Every engagement includes documentation and hands-on training so your team is confident using what I build from day one.'
      },
      {
        question: 'What ongoing support do you provide?',
        answer: 'Flexible support including system monitoring, user support, regular health checks, and continuous optimization to keep your org delivering value as you grow.'
      },
      {
        question: 'How much does Salesforce development cost?',
        answer: 'It varies with scope. I offer a free consultation to understand your needs and provide transparent pricing. Most engagements pay for themselves through the time and rework they eliminate.'
      }
    ]
  },

  schema: {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfessionalService",
        "@id": "https://www.danielrayrangel.com/#business",
        "name": "Exhale Software Solutions",
        "image": "https://www.danielrayrangel.com/Exhale.Daniel.Ray.Rangel.png",
        "url": "https://www.danielrayrangel.com",
        "telephone": "+1-480-282-0569",
        "email": "daniel@danielrayrangel.com",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Phoenix",
          "addressRegion": "AZ",
          "addressCountry": "US"
        },
        "areaServed": [
          { "@type": "State", "name": "Arizona" },
          { "@type": "City", "name": "Phoenix" },
          { "@type": "City", "name": "Scottsdale" },
          { "@type": "City", "name": "Mesa" }
        ],
        "founder": { "@id": "https://www.danielrayrangel.com/#daniel" }
      },
      {
        "@type": "Person",
        "@id": "https://www.danielrayrangel.com/#daniel",
        "name": "Daniel Rangel",
        "jobTitle": "Senior Salesforce Developer",
        "url": "https://www.danielrayrangel.com",
        "worksFor": { "@id": "https://www.danielrayrangel.com/#business" },
        "sameAs": [
          "https://www.linkedin.com/in/danielrayrangel/",
          "https://www.facebook.com/exhalesoftware/"
        ]
      },
      {
        "@type": "Service",
        "name": "Salesforce Development Services",
        "serviceType": "Salesforce Development",
        "description": "Senior Salesforce development — Apex, Lightning Web Components, integrations, and data governance for Arizona businesses",
        "url": "https://www.danielrayrangel.com/services/salesforce",
        "provider": { "@id": "https://www.danielrayrangel.com/#daniel" },
        "areaServed": { "@type": "State", "name": "Arizona" }
      }
    ]
  }
};
